/* A Generic Modal Component
  Desktop: Displays a white modal on a dark background
  Mobile: Full-page white modal
  Props (check defaultProps for more):
  -> children: content to display
  -> open: boolean, whether the modal is open or not
  -> close: function, called when modal close triggered
  -> animSpeed: object, {open: 'xxms', close: 'xxms'}
  The component body also accepts classnames as a prop and should
  work with styled components
*/

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Portal } from 'react-portal';
import CloseIcon from 'views/ui/icons/close-icon';

import { Theme } from 'ui_system/theme';

const Wrapper = styled.div`
  align-items: center;
  background: rgba(31, 37, 50, 0.5);
  bottom: 0;
  display: grid;
  left: 0%;
  margin: 0 auto;
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: scroll;
  position: fixed;
  top: 0;
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  width: 100vw;
  z-index: 100000;
  @media (max-width: ${Theme.mobile.phoneWidth}px) {
    align-items: flex-start;
  }
`;

// ANIM SPEED
// Checks for and uses animSpeed prop on <Modal />
const setSpeed = (defaultSpeed, param, animSpeed) => {
  if (animSpeed) {
    return animSpeed[param];
  }
  return defaultSpeed;
};

const ModalBody = styled.div`
  background: white;
  margin: 0 auto;
  ${(props) => props.mobileBackNav && 'margin-top: 48px;'}
  max-width: ${(props) => props.maxWidth || '100vw'};
  min-height: 100vh;
  padding: ${(props) => props.padding || 0};
  top: 0;
  width: 100vw;
  z-index: 99999;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    min-height: initial;
    width: ${(props) => props.width || '600px'};
  }

  /* General Transition rules */
  -webkit-transition: all ${(props) => props.animSpeed}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);
  -moz-transition: all ${(props) => props.animSpeed}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);
  transition: all ${(props) => props.animSpeed}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);

  /* Scale in */
  transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -webkit-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -moz-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -ms-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};

  /* Fade in */
  opacity: ${(props) => (props.open ? 1 : 0)};
  will-change: transform, opacity;
`;

// Mobile CloseButton is inside the full-page modal
// Desktop: CloseButton is on top-right corner of modal
const CloseButton = styled.div`
  align-items: center;
  background: white;
  border: 1px solid white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  position: absolute;
  right: 0px;
  top: 0px;
  width: 48px;

  svg {
    fill: ${Theme.colors.lightText};
    transform: scale(1.333);
  }

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    background: ${Theme.colors.black};
    height: 32px;
    right: -16px;
    top: -16px;
    width: 32px;

    svg {
      fill: white;
      transform: scale(1);
    }
  }

  /* Required for Mobile Safari */
  z-index: 102;
`;

function Modal(props) {
  const {
    open,
    close,
    children,
    className,
    hideClose,
    MobileBackNav,
    animSpeed,
    width,
    padding,
    maxWidth,
    layout: Layout,
  } = props;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => (document.body.style.overflow = 'initial');
    }
    return undefined;
  }, []);

  return (
    <Portal isOpen={open}>
      <Wrapper open={open} onClick={close}>
        {MobileBackNav}
        <ModalBody
          className={className}
          width={width}
          maxWidth={maxWidth}
          open={open}
          padding={padding}
          onClick={(e) => e.stopPropagation()}
          animSpeed={
            open
              ? setSpeed('260ms', 'open', animSpeed)
              : setSpeed('200ms', 'close', animSpeed)
          }
          mobileBackNav={MobileBackNav}
          data-testid="modal-window"
        >
          {hideClose ? null : (
            <CloseButton onClick={close} data-testid="close-modal">
              <CloseIcon />
            </CloseButton>
          )}
          {Layout ? <Layout>{children}</Layout> : children}
        </ModalBody>
      </Wrapper>
    </Portal>
  );
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  animSpeed: PropTypes.object,
  hideClose: PropTypes.bool,
  MobileBackNav: PropTypes.element,
  children: PropTypes.node,
  className: PropTypes.string,
  close: PropTypes.func,
  maxWidth: PropTypes.string,
  width: PropTypes.string,
  padding: PropTypes.string,
};

Modal.defaultProps = {
  open: false,
  animSpeed: null,
  hideClose: false,
  MobileBackNav: null,
  children: null,
  className: null,
  close: () => console.log('Provide a close function prop'),
};

export default Modal;

export const createModalWithTrigger =
  (ModalContent) =>
  ({
    key,
    children,
    open: startOpen,
    onOpen = () => {},
    onClose = () => {},
    ...modalProps
  }) => {
    const [isOpen, setOpen] = useState(startOpen);
    const open = () => {
      setOpen(true);
      onOpen();
    };
    const close = () => {
      setOpen(false);
      onClose();
    };
    const trigger = React.Children.map(children, (child) =>
      React.cloneElement(child, {
        onClick: (...rest) => {
          open();
          if (child.props.onClick) {
            child.props.onClick(...rest);
          }
        },
      })
    );
    return (
      <React.Fragment key={key}>
        <Modal
          open={startOpen !== undefined ? startOpen : isOpen}
          close={close}
          {...modalProps}
        >
          <ModalContent close={close} {...modalProps} />
        </Modal>
        {trigger}
      </React.Fragment>
    );
  };
